import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { getAllRequests } from "../../redux/requestSlice";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { parseBackendDate } from "../../utils/dateUtils";
import { FiCalendar, FiClock, FiUser, FiFilter, FiPlus } from "react-icons/fi";
import FreeSessionModal from "./FreeSessionModal";

const API_URL = process.env.REACT_APP_API_URL;

const STATUS_LABELS = {
  pending:   { label: "Pending",   color: "bg-yellow-100 text-yellow-700" },
  active:    { label: "Active",    color: "bg-green-100 text-green-700" },
  ongoing:   { label: "Ongoing",   color: "bg-green-100 text-green-700" },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-700" },
  accepted:  { label: "Accepted",  color: "bg-green-100 text-green-700" },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
  canceled:  { label: "Cancelled", color: "bg-red-100 text-red-700" },
  rejected:  { label: "Rejected",  color: "bg-red-100 text-red-700" },
};

const formatDate = (date) => {
  if (!date) return "-";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTime = (date) => {
  if (!date) return "-";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

// availabilityMap: { [availabilityId]: { startDate, endDate } }
const getSlotDates = (req, availabilityMap = {}) => {
  // 1. availabilityIds'ten map'e bak
  const availId = req.availabilityIds?.[0];
  if (availId) {
    const idStr = typeof availId === "object" ? availId._id : availId;
    const slot = availabilityMap[idStr];
    if (slot?.startDate) {
      const start = parseBackendDate(slot.startDate);
      const end = slot.endDate ? parseBackendDate(slot.endDate) : (start ? new Date(start.getTime() + 60 * 60 * 1000) : null);
      return { start, end, source: "slot" };
    }
    // availabilityId obje olarak populate geldiyse
    if (typeof availId === "object" && availId.startDate) {
      const start = parseBackendDate(availId.startDate);
      const end = availId.endDate ? parseBackendDate(availId.endDate) : (start ? new Date(start.getTime() + 60 * 60 * 1000) : null);
      return { start, end, source: "slot" };
    }
  }

  // 2. request üzerinde doğrudan startDate varsa
  if (req.startDate) {
    const start = parseBackendDate(req.startDate);
    const end = req.endDate ? parseBackendDate(req.endDate) : (start ? new Date(start.getTime() + 60 * 60 * 1000) : null);
    return { start, end, source: "slot" };
  }

  // 3. selectedSlot
  if (req.selectedSlot?.startTime || req.selectedSlot?.startDate) {
    const start = parseBackendDate(req.selectedSlot.startTime || req.selectedSlot.startDate);
    const end = parseBackendDate(req.selectedSlot.endTime || req.selectedSlot.endDate) || (start ? new Date(start.getTime() + 60 * 60 * 1000) : null);
    return { start, end, source: "slot" };
  }

  // 4. Son çare: createdAt
  const start = parseBackendDate(req.createdAt);
  return { start, end: start ? new Date(start.getTime() + 60 * 60 * 1000) : null, source: "createdAt" };
};

const getTherapistId = (req) => {
  const t = req.therapistId;
  if (!t) return null;
  return typeof t === "object" ? t._id : t;
};

const getTherapistName = (req) => {
  const t = req.therapistId;
  if (!t) return "-";
  if (typeof t === "object") return `${t.name || ""} ${t.surname || ""}`.trim() || t.email || "-";
  return String(t);
};

const getClientName = (req) => {
  const u = req.userId;
  if (!u) return "-";
  if (typeof u === "object") return `${u.name || ""} ${u.surname || ""}`.trim() || u.email || "-";
  return String(u);
};

const DATE_FILTERS = [
  { key: "all",   label: "All" },
  { key: "today", label: "Today" },
  { key: "week",  label: "This Week" },
  { key: "month", label: "This Month" },
];

const AdminSessions = () => {
  const dispatch = useDispatch();
  const { requests = [], loading, error } = useSelector((state) => state.requests);

  const [dateFilter, setDateFilter] = useState("all");
  const [therapistFilter, setTherapistFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFreeSessionModal, setShowFreeSessionModal] = useState(false);
  // availabilityId → { startDate, endDate }
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [availLoading, setAvailLoading] = useState(false);

  useEffect(() => {
    dispatch(getAllRequests());
  }, [dispatch]);

  // Tüm requestler gelince unique terapistlerin availability'lerini çek
  useEffect(() => {
    if (!requests.length) return;

    const therapistIds = [...new Set(requests.map(getTherapistId).filter(Boolean))];
    if (!therapistIds.length) return;

    const token = localStorage.getItem("token");
    setAvailLoading(true);

    Promise.all(
      therapistIds.map((id) =>
        axios
          .get(`${API_URL}/availabilities/therapist/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => res.data)
          .catch(() => [])
      )
    ).then((results) => {
      const map = {};
      results.flat().forEach((slot) => {
        if (slot._id) map[slot._id] = slot;
      });
      setAvailabilityMap(map);
      setAvailLoading(false);
    });
  }, [requests]);

  const therapistOptions = useMemo(() => {
    const names = new Map();
    requests.forEach((req) => {
      const id = getTherapistId(req);
      const t = req.therapistId;
      const name = t && typeof t === "object" ? (`${t.name || ""} ${t.surname || ""}`.trim() || t.email) : String(t || "");
      if (id) names.set(id, name);
    });
    return Array.from(names.entries()).map(([id, name]) => ({ id, name }));
  }, [requests]);

  const filtered = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay()); startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return requests
      .filter((req) => {
        const { start } = getSlotDates(req, availabilityMap);
        if (!start) return false;

        if (dateFilter === "today" && start < startOfDay) return false;
        if (dateFilter === "week"  && start < startOfWeek) return false;
        if (dateFilter === "month" && start < startOfMonth) return false;

        if (therapistFilter !== "all" && getTherapistId(req) !== therapistFilter) return false;
        if (statusFilter !== "all") {
          const s = req.status;
          const isCancelled = statusFilter === "cancelled" && (s === "cancelled" || s === "canceled");
          if (!isCancelled && s !== statusFilter) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const { start: sa } = getSlotDates(a, availabilityMap);
        const { start: sb } = getSlotDates(b, availabilityMap);
        return (sb?.getTime() || 0) - (sa?.getTime() || 0);
      });
  }, [requests, availabilityMap, dateFilter, therapistFilter, statusFilter]);

  const isLoading = loading || availLoading;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <div className="md:shrink-0 md:mr-6">
        <AdminSideBar />
      </div>

      <div className="flex-1 p-4 md:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-md p-6 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
            <button
              onClick={() => setShowFreeSessionModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Create Free Session
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 items-center">
            <FiFilter className="text-gray-400 w-4 h-4" />

            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {DATE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setDateFilter(f.key)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    dateFilter === f.key ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <select
              value={therapistFilter}
              onChange={(e) => setTherapistFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="all">All Therapists</option>
              {therapistOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="confirmed">Confirmed</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>

            <span className="ml-auto text-sm text-gray-400">{filtered.length} sessions</span>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-8">{String(error)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No sessions found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-semibold">Date</th>
                    <th className="pb-3 pr-4 font-semibold">Time</th>
                    <th className="pb-3 pr-4 font-semibold">Therapist</th>
                    <th className="pb-3 pr-4 font-semibold">Client</th>
                    <th className="pb-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((req) => {
                    const { start, end, source } = getSlotDates(req, availabilityMap);
                    const statusInfo = STATUS_LABELS[req.status] || { label: req.status || "Unknown", color: "bg-gray-100 text-gray-600" };
                    const isEstimated = source === "createdAt";

                    return (
                      <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 pr-4">
                          <div className={`flex items-center gap-1.5 ${isEstimated ? "text-gray-400" : "text-gray-700"}`}>
                            <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                            {formatDate(start)}
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <div className={`flex items-center gap-1.5 ${isEstimated ? "text-gray-400 italic" : "text-gray-700"}`}>
                            <FiClock className="w-3.5 h-3.5 text-gray-400" />
                            {isEstimated ? "—" : `${formatTime(start)}${end ? ` – ${formatTime(end)}` : ""}`}
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-1.5 font-medium text-gray-900">
                            <FiUser className="w-3.5 h-3.5 text-gray-400" />
                            {getTherapistName(req)}
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {getClientName(req)}
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showFreeSessionModal && (
        <FreeSessionModal
          onClose={() => setShowFreeSessionModal(false)}
          onSuccess={() => dispatch(getAllRequests())}
        />
      )}
    </div>
  );
};

export default AdminSessions;
